/**
 * One opening chip in the assistant's empty state.
 *
 * `label` and `message` are i18n KEYS, not text. `message` is resolved at send
 * time rather than at render time: the assistant detects the reply language
 * from the user's message, so a chip that sent hardcoded English started every
 * conversation in English regardless of the UI language.
 */
export interface ChatSuggestion {
  id: string;
  icon: string;
  label: string;
  message: string;
}

/**
 * The chips an owner-like workspace opens on. Also the fallback for any type
 * without its own set — they are the most general of the three.
 */
export const OWNER_SUGGESTIONS: readonly ChatSuggestion[] = [
  {
    id: 'overview',
    icon: 'dashboard',
    label: 'aiAssistant.suggestions.overview',
    message: 'aiAssistant.prompts.overview'
  },
  {
    id: 'cards',
    icon: 'pending_actions',
    label: 'aiAssistant.suggestions.cards',
    message: 'aiAssistant.prompts.cards'
  },
  {
    id: 'cashflow',
    icon: 'paid',
    label: 'aiAssistant.suggestions.cashflow',
    message: 'aiAssistant.prompts.cashflow'
  },
  {
    id: 'overdue',
    icon: 'schedule',
    label: 'aiAssistant.suggestions.overdue',
    message: 'aiAssistant.prompts.overdue'
  }
];

export const SYNDIC_SUGGESTIONS: readonly ChatSuggestion[] = [
  {
    id: 'buildings',
    icon: 'apartment',
    label: 'aiAssistant.suggestions.buildings',
    message: 'aiAssistant.prompts.buildings'
  },
  {
    id: 'buildingCharges',
    icon: 'receipt_long',
    label: 'aiAssistant.suggestions.buildingCharges',
    message: 'aiAssistant.prompts.buildingCharges'
  },
  {
    id: 'connectionRequests',
    icon: 'how_to_reg',
    label: 'aiAssistant.suggestions.connectionRequests',
    message: 'aiAssistant.prompts.connectionRequests'
  }
];

export const PROVIDER_SUGGESTIONS: readonly ChatSuggestion[] = [
  {
    id: 'myJobs',
    icon: 'handyman',
    label: 'aiAssistant.suggestions.myJobs',
    message: 'aiAssistant.prompts.myJobs'
  },
  {
    id: 'quotesToSend',
    icon: 'request_quote',
    label: 'aiAssistant.suggestions.quotesToSend',
    message: 'aiAssistant.prompts.quotesToSend'
  },
  {
    id: 'myProfile',
    icon: 'badge',
    label: 'aiAssistant.suggestions.myProfile',
    message: 'aiAssistant.prompts.myProfile'
  }
];

const BY_TYPE: Partial<Record<string, readonly ChatSuggestion[]>> = {
  syndic: SYNDIC_SUGGESTIONS,
  service_provider: PROVIDER_SUGGESTIONS
};

/**
 * Opening chips for a workspace type.
 *
 * A chip is a promise: tapping it sends that exact message, so it must map to
 * something the workspace's tool catalogue can actually answer. Offering
 * "overdue rent" to a syndic — who has no leases, only buildings and charge
 * statements — makes the assistant open on a question it must refuse, which is
 * a worse first impression than no chips at all.
 *
 * Types with no entry fall back to the owner set rather than to nothing: an
 * empty state with no chips gives the user no way in, and the owner questions
 * are the most general of the three sets.
 *
 * The parameter is a plain `string`, not `WorkspaceType`, and deliberately so.
 * The codebase carries TWO `Workspace` interfaces: the one in
 * `shared/definitions/workspace.ts` types `type` as the union, the one the
 * WorkspaceStore actually serves (`core/services/auth/workspace.service.types`)
 * types it as `string`. Unifying them is frozen debt (CLAUDE.md: UI adapters
 * only), so the adapting happens here — which costs nothing, because the
 * fallback below already handles a value outside the union at runtime.
 */
export function suggestionsForWorkspaceType(
  type: string | null | undefined
): readonly ChatSuggestion[] {
  if (!type) return OWNER_SUGGESTIONS;
  return BY_TYPE[type] ?? OWNER_SUGGESTIONS;
}

/**
 * i18n key for the empty-state hint, per workspace type.
 *
 * Found in browser recipe: the panel opened on a provider showing "ask about a
 * lease's rent, a property's expenses this year" — three things a provider
 * workspace does not contain. Same defect as owner chips shown to a syndic,
 * one layer down. The hint tells the user what to ask, so it has to describe
 * what their catalogue can answer.
 *
 * Same `string` parameter, same reason as `suggestionsForWorkspaceType`.
 */
export function emptyHintKeyFor(type: string | null | undefined): string {
  if (type === 'syndic') return 'aiAssistant.empty.hintSyndic';
  if (type === 'service_provider') return 'aiAssistant.empty.hintProvider';
  return 'aiAssistant.empty.hint';
}

/** i18n key for the composer placeholder. Same rule as the hint above. */
export function inputPlaceholderKeyFor(type: string | null | undefined): string {
  if (type === 'syndic') return 'aiAssistant.inputPlaceholderSyndic';
  if (type === 'service_provider') return 'aiAssistant.inputPlaceholderProvider';
  return 'aiAssistant.inputPlaceholder';
}
